import { Area, Player } from "@/lib/types";
import dagre from "@dagrejs/dagre";
import { type Node, type Edge } from "@xyflow/react";
import { NodeDataArea, NodeDataPlayer } from "./AreaPageTypes";
import { SelectedMediaPreview } from "../shared/SelectedMediaPreview";
import { t } from "i18next";

const dagreGraph = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));

export const nodeWidth = 250;
export const nodeHeight = 72;

const position = { x: 0, y: 0 };

export const getNodes = (areaData: Area[], playerData: Player[]) => {
  const initialNodes: Node[] = areaData.map(
    (area: Area) =>
      ({
        id: `area-${area.intId}`,
        type: "area",
        data: {
          type: "area",
          label: (
            <div>
              <div>{area.name}</div>
              {(area.contentintId || area.contentGroupintId) && (
                <div>
                  <SelectedMediaPreview contentIntId={area.contentintId} contentGroupIntId={area.contentGroupintId} />
                </div>
              )}
            </div>
          ),
          idString: area.id,
          intId: area.intId,
          timezone: area.timezone,
          motherAreaintId: area.motherAreaintId,
          name: area.name,
          defaultContentintId: area.defaultContentintId,
          areaTypeId: area.areaTypeintId,
          overwriteSub: area.overwriteSub,
        } satisfies NodeDataArea,
        position,
      } satisfies Node)
  );
  initialNodes.push(
    ...playerData
      .filter((player: Player) => player.areaintId)
      .map(
        (player: Player) =>
          ({
            id: `player-${player.intId}`,
            type: "player",
            data: {
              type: "player",
              label: `${player.name} (${t("entities.player")})`,
              idString: player.id,
              intId: player.intId,
              timezone: player.timezone,
              name: player.name,
            } satisfies NodeDataPlayer,
            connectable: false,
            position,
          } satisfies Node)
      )
  );
  return initialNodes;
};

export const getEdges = (areaData: Area[], playerData: Player[]) => {
  const initialEdges: Edge[] = areaData
    .filter((area: Area) => area.motherAreaintId)
    .map(
      (area: Area) =>
        ({
          id: `${area.intId}-${area.motherAreaintId}`,
          source: `area-${area.motherAreaintId}`,
          target: `area-${area.intId}`,
        } satisfies Edge)
    );
  initialEdges.push(
    ...playerData
      .filter((player: Player) => player.areaintId)
      .map(
        (player: Player) =>
          ({
            id: `${player.intId}-${player.areaintId}`,
            source: `area-${player.areaintId}`,
            target: `player-${player.intId}`,
          } satisfies Edge)
      )
  );
  return initialEdges;
};

export const getLayoutedElements = (nodes: Node[], edges: Edge[]) => {
  dagreGraph.setGraph({ rankdir: "TB" });

  nodes.forEach((node: Node) => {
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  });

  edges.forEach((edge: Edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const newNodes = nodes.map((node: Node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    const newNode = {
      ...node,
      targetPosition: "top",
      sourcePosition: "bottom",
      // We are shifting the dagre node position (anchor=center center) to the top left
      // so it matches the React Flow node anchor point (top left).
      position: {
        x: nodeWithPosition.x - nodeWidth / 2,
        y: nodeWithPosition.y - nodeHeight / 2,
      },
    };

    return newNode;
  });

  return { nodes: newNodes as Node[], edges };
};
